/**
 * Global context for managing game statuses across the app.
 *
 * This context provides:
 * - Normalized status cache keyed by puzzle_instance_id
 * - Scope-based registration and fetch scheduling
 * - SWR-style freshness checks and scoped revalidation
 * - Local optimistic status updates (with optional cross-tab sync)
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { useUser, useAuth } from '@clerk/clerk-react';
import { getSessionStatuses } from '../api/gameplay';
import { usePuzzleInstances } from './PuzzleInstancesContext';

const GameStatusesContext = createContext(null);

const FETCH_COALESCE_MS = 50;
const MAX_IDS_PER_REQUEST = 300;
const SOFT_STALE_TTL_MS = 2 * 60 * 1000;
const HARD_STALE_TTL_MS = 10 * 60 * 1000;

const STATUS_CHANNEL_NAME = 'game-status-updated';

const PRIORITY_WEIGHT = {
  low: 1,
  medium: 2,
  high: 3,
};

const toFrontendStatus = (status) => {
  if (status === 'won' || status === 'lost' || status === 'completed') {
    return 'finished';
  }
  if (status === null || status === undefined) {
    return 'not_started';
  }
  return status;
};

const toGameDateKey = (gameSlug, date) => `${gameSlug}:${date}`;

const chunkArray = (items, chunkSize) => {
  if (items.length <= chunkSize) return [items];
  const chunks = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
};

const isSubset = (subset, superset) => {
  if (subset.size > superset.size) return false;
  for (const value of subset) {
    if (!superset.has(value)) return false;
  }
  return true;
};

const hashGameData = (gameData = []) => JSON.stringify(
  gameData.map((group) => ({
    gameId: group.gameId,
    dates: (group.games || []).map((game) => game.date),
  })),
);

export function GameStatusesProvider({ children }) {
  const { isSignedIn } = useUser();
  const { getToken } = useAuth();
  const { getPuzzleInstanceId, isLoading: isPuzzleInstancesLoading } = usePuzzleInstances();

  // Normalized cache keyed by puzzle_instance_id
  const statusByPuzzleIdRef = useRef(new Map());
  // Derived index for fast reads by game/date
  const puzzleIdByGameDateRef = useRef(new Map());
  const statusByGameDateRef = useRef(new Map());

  // scopeKey -> scope state
  const scopeStateRef = useRef(new Map());

  // request scheduler + in-flight tracking
  const fetchTimerRef = useRef(null);
  const immediateFlushQueuedRef = useRef(false);
  const pendingFetchPriorityRef = useRef('low');
  const requestVersionRef = useRef(0);
  const inFlightRef = useRef(new Map()); // requestKey -> { idsSet, promise }

  // Optional cross-tab sync
  const broadcastChannelRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);

  const bumpVersion = useCallback(() => {
    setVersion((value) => value + 1);
  }, []);

  const getScope = useCallback((scopeKey) => {
    if (!scopeKey) return null;
    return scopeStateRef.current.get(scopeKey) || null;
  }, []);

  const getScopeFreshness = useCallback((scopeKey) => {
    const scope = getScope(scopeKey);
    if (!scope) {
      return {
        hasData: false,
        hasFetchedOnce: false,
        isLoading: false,
        isSoftStale: true,
        isHardStale: true,
        lastFetchedAt: 0,
      };
    }

    const now = Date.now();
    const age = scope.lastFetchedAt > 0 ? now - scope.lastFetchedAt : Infinity;

    return {
      hasData: scope.hasAnyData,
      hasFetchedOnce: scope.hasFetchedOnce,
      isLoading: scope.loadingCount > 0,
      isSoftStale: age >= SOFT_STALE_TTL_MS,
      isHardStale: age >= HARD_STALE_TTL_MS,
      lastFetchedAt: scope.lastFetchedAt,
    };
  }, [getScope]);

  const applyStatusForPuzzleId = useCallback(({
    puzzleInstanceId,
    frontendStatus,
    updatedAt,
    source,
    requestVersion,
  }) => {
    const existing = statusByPuzzleIdRef.current.get(puzzleInstanceId);
    if (existing && existing.requestVersion > requestVersion) {
      return false;
    }

    statusByPuzzleIdRef.current.set(puzzleInstanceId, {
      status: frontendStatus,
      updatedAt,
      source,
      requestVersion,
    });

    let updated = false;
    for (const scope of scopeStateRef.current.values()) {
      const mappings = scope.mappingByPuzzleId.get(puzzleInstanceId);
      if (!mappings || mappings.length === 0) continue;
      for (const mapping of mappings) {
        const gameDateKey = toGameDateKey(mapping.gameSlug, mapping.date);
        puzzleIdByGameDateRef.current.set(gameDateKey, puzzleInstanceId);
        statusByGameDateRef.current.set(gameDateKey, frontendStatus);
        updated = true;
      }
      scope.hasAnyData = true;
    }

    return updated;
  }, []);

  const syncStatusFromGameDate = useCallback((gameSlug, date, status, source, shouldBroadcast = false) => {
    const gameDateKey = toGameDateKey(gameSlug, date);
    const puzzleInstanceId = puzzleIdByGameDateRef.current.get(gameDateKey);
    const now = Date.now();
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;

    if (puzzleInstanceId) {
      applyStatusForPuzzleId({
        puzzleInstanceId,
        frontendStatus: status,
        updatedAt: now,
        source,
        requestVersion,
      });
    } else {
      // If the puzzle ID is not mapped yet, still keep derived status cache hot.
      statusByGameDateRef.current.set(gameDateKey, status);
    }

    bumpVersion();

    if (shouldBroadcast && broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({
        gameSlug,
        date,
        status,
      });
    }
  }, [applyStatusForPuzzleId, bumpVersion]);

  const registerScope = useCallback((scopeKey, consumerId, gameData, priority = 'medium') => {
    if (!scopeKey || !consumerId || !gameData || gameData.length === 0) return;

    const now = Date.now();
    const existing = scopeStateRef.current.get(scopeKey);
    const next = existing || {
      key: scopeKey,
      consumers: new Set(),
      mappingByPuzzleId: new Map(),
      idsSet: new Set(),
      hash: '',
      priority: 'low',
      hasFetchedOnce: false,
      hasAnyData: false,
      lastFetchedAt: 0,
      loadingCount: 0,
    };

    next.consumers.add(consumerId);
    next.priority = PRIORITY_WEIGHT[priority] >= PRIORITY_WEIGHT[next.priority] ? priority : next.priority;

    const nextHash = hashGameData(gameData);
    if (next.hash !== nextHash) {
      const mappingByPuzzleId = new Map();
      const idsSet = new Set();

      for (const gameGroup of gameData) {
        for (const game of gameGroup.games || []) {
          const puzzleInstanceId = getPuzzleInstanceId(gameGroup.gameId, game.date);
          if (!puzzleInstanceId) continue;

          idsSet.add(puzzleInstanceId);
          const mappings = mappingByPuzzleId.get(puzzleInstanceId) || [];
          mappings.push({ gameSlug: gameGroup.gameId, date: game.date });
          mappingByPuzzleId.set(puzzleInstanceId, mappings);

          const gameDateKey = toGameDateKey(gameGroup.gameId, game.date);
          puzzleIdByGameDateRef.current.set(gameDateKey, puzzleInstanceId);
        }
      }

      next.mappingByPuzzleId = mappingByPuzzleId;
      next.idsSet = idsSet;
      next.hash = nextHash;
      next.hasFetchedOnce = false;
      next.lastFetchedAt = 0;

      let hasAnyData = false;
      for (const puzzleId of idsSet) {
        const entry = statusByPuzzleIdRef.current.get(puzzleId);
        if (!entry) continue;
        hasAnyData = true;
        const mappings = mappingByPuzzleId.get(puzzleId) || [];
        for (const mapping of mappings) {
          const gameDateKey = toGameDateKey(mapping.gameSlug, mapping.date);
          statusByGameDateRef.current.set(gameDateKey, entry.status);
        }
      }
      next.hasAnyData = hasAnyData;
      next.updatedAt = now;
    }

    scopeStateRef.current.set(scopeKey, next);
  }, [getPuzzleInstanceId]);

  const unregisterScopeConsumer = useCallback((scopeKey, consumerId) => {
    if (!scopeKey || !consumerId) return;
    const scope = scopeStateRef.current.get(scopeKey);
    if (!scope) return;

    scope.consumers.delete(consumerId);
    // Keep cached scope data for future reuse, but it is no longer active for fetch unions.
    scopeStateRef.current.set(scopeKey, scope);
  }, []);

  const getActiveScopeEntries = useCallback(() => {
    const entries = [];
    for (const [scopeKey, scope] of scopeStateRef.current.entries()) {
      if (!scope || scope.consumers.size === 0 || scope.idsSet.size === 0) continue;
      entries.push([scopeKey, scope]);
    }
    return entries;
  }, []);

  const setScopeLoading = useCallback((scopeKeys, delta) => {
    let anyLoading = false;

    for (const scope of scopeStateRef.current.values()) {
      if (scope.loadingCount > 0) {
        anyLoading = true;
      }
    }

    for (const scopeKey of scopeKeys) {
      const scope = scopeStateRef.current.get(scopeKey);
      if (!scope) continue;
      scope.loadingCount = Math.max(0, scope.loadingCount + delta);
      scopeStateRef.current.set(scopeKey, scope);
    }

    for (const scope of scopeStateRef.current.values()) {
      if (scope.loadingCount > 0) {
        anyLoading = true;
        break;
      }
    }

    setIsLoading(anyLoading);
  }, []);

  const runFetchForIds = useCallback(async ({
    ids,
    scopeKeys,
    snapshotByPuzzleId,
    requestVersion,
    priority,
  }) => {
    if (!ids || ids.length === 0) return;

    setScopeLoading(scopeKeys, 1);
    setError(null);

    const idsSet = new Set(ids);
    for (const inFlight of inFlightRef.current.values()) {
      if (isSubset(idsSet, inFlight.idsSet)) {
        try {
          await inFlight.promise;
        } finally {
          setScopeLoading(scopeKeys, -1);
        }
        return;
      }
    }

    const requestKey = `${requestVersion}:${ids.join(',')}`;

    const promise = (async () => {
      const token = await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null);
      const result = await getSessionStatuses(ids, token);

      if (!result.success || !result.statuses) {
        throw new Error(result.error || 'Failed to fetch game statuses');
      }

      const now = Date.now();
      let updated = false;

      for (const item of result.statuses) {
        const puzzleInstanceId = String(item.puzzle_instance_id);
        const frontendStatus = toFrontendStatus(item.status);

        // Only apply response for IDs that were in the request snapshot.
        if (!snapshotByPuzzleId.has(puzzleInstanceId)) {
          continue;
        }

        const didUpdate = applyStatusForPuzzleId({
          puzzleInstanceId,
          frontendStatus,
          updatedAt: now,
          source: 'server',
          requestVersion,
        });

        updated = updated || didUpdate;
      }

      for (const scopeKey of scopeKeys) {
        const scope = scopeStateRef.current.get(scopeKey);
        if (!scope) continue;
        scope.hasFetchedOnce = true;
        scope.lastFetchedAt = now;
        scope.priority = PRIORITY_WEIGHT[priority] >= PRIORITY_WEIGHT[scope.priority] ? priority : scope.priority;
        scopeStateRef.current.set(scopeKey, scope);
      }

      if (updated) {
        bumpVersion();
      } else {
        // Still bump so hooks can react to hasFetchedOnce/loading transitions.
        setVersion((value) => value + 1);
      }
    })();

    inFlightRef.current.set(requestKey, {
      idsSet,
      promise,
    });

    try {
      await promise;
    } catch (fetchError) {
      console.error('Error fetching game statuses:', fetchError);
      setError(fetchError.message);
    } finally {
      inFlightRef.current.delete(requestKey);
      setScopeLoading(scopeKeys, -1);
    }
  }, [applyStatusForPuzzleId, bumpVersion, getToken, setScopeLoading]);

  const flushScheduledFetch = useCallback(async () => {
    fetchTimerRef.current = null;

    if (isPuzzleInstancesLoading) {
      return;
    }

    const activeScopes = getActiveScopeEntries();
    if (activeScopes.length === 0) {
      return;
    }

    const unionIds = new Set();
    const scopeKeys = [];
    const snapshotByPuzzleId = new Map();

    for (const [scopeKey, scope] of activeScopes) {
      scopeKeys.push(scopeKey);
      for (const puzzleId of scope.idsSet) {
        unionIds.add(puzzleId);
        if (!snapshotByPuzzleId.has(puzzleId)) {
          snapshotByPuzzleId.set(puzzleId, []);
        }
        const current = snapshotByPuzzleId.get(puzzleId);
        const mappings = scope.mappingByPuzzleId.get(puzzleId) || [];
        for (const mapping of mappings) {
          current.push(mapping);
        }
      }
    }

    const allIds = Array.from(unionIds);
    if (allIds.length === 0) {
      return;
    }

    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;

    const priority = pendingFetchPriorityRef.current;
    pendingFetchPriorityRef.current = 'low';

    const chunks = chunkArray(allIds, MAX_IDS_PER_REQUEST);
    await Promise.all(
      chunks.map((idsChunk, index) => {
        const perChunkVersion = requestVersion + index;
        return runFetchForIds({
          ids: idsChunk,
          scopeKeys,
          snapshotByPuzzleId,
          requestVersion: perChunkVersion,
          priority,
        });
      }),
    );
  }, [getActiveScopeEntries, isPuzzleInstancesLoading, runFetchForIds]);

  const scheduleFetch = useCallback((priority = 'medium', options = {}) => {
    const { immediate = false } = options;

    if (PRIORITY_WEIGHT[priority] > PRIORITY_WEIGHT[pendingFetchPriorityRef.current]) {
      pendingFetchPriorityRef.current = priority;
    }

    // For first-load/forced refreshes, dispatch immediately instead of relying on
    // the coalescing timer so status fetches cannot be deferred by later activity.
    if (immediate) {
      if (fetchTimerRef.current) {
        clearTimeout(fetchTimerRef.current);
        fetchTimerRef.current = null;
      }
      if (immediateFlushQueuedRef.current) {
        return;
      }
      immediateFlushQueuedRef.current = true;
      Promise.resolve().then(() => {
        immediateFlushQueuedRef.current = false;
        flushScheduledFetch();
      });
      return;
    }

    if (immediateFlushQueuedRef.current) {
      return;
    }

    if (fetchTimerRef.current) {
      return;
    }

    fetchTimerRef.current = setTimeout(() => {
      flushScheduledFetch();
    }, FETCH_COALESCE_MS);
  }, [flushScheduledFetch]);

  const refreshActiveScopesIfStale = useCallback((trigger = 'focus') => {
    const activeScopes = getActiveScopeEntries();
    if (activeScopes.length === 0) return;

    let shouldRefresh = false;
    let priority = 'low';

    for (const [scopeKey] of activeScopes) {
      const freshness = getScopeFreshness(scopeKey);
      if (freshness.isHardStale) {
        shouldRefresh = true;
        priority = 'medium';
        break;
      }
      if (freshness.isSoftStale) {
        shouldRefresh = true;
      }
    }

    if (shouldRefresh) {
      scheduleFetch(priority);
    }
  }, [getActiveScopeEntries, getScopeFreshness, scheduleFetch]);

  const ensureScopeData = useCallback((scopeKey, options = {}) => {
    const { priority = 'medium', force = false } = options;

    const freshness = getScopeFreshness(scopeKey);
    if (force) {
      scheduleFetch(priority, { immediate: true });
      return;
    }

    if (!freshness.hasFetchedOnce) {
      scheduleFetch(priority, { immediate: true });
      return;
    }

    if (freshness.isSoftStale) {
      scheduleFetch(priority);
    }
  }, [getScopeFreshness, scheduleFetch]);

  const requestScopeRefresh = useCallback((scopeKey, options = {}) => {
    const { priority = 'low' } = options;
    ensureScopeData(scopeKey, { priority, force: true });
  }, [ensureScopeData]);

  const getGameStatus = useCallback((gameSlug, date) => {
    const gameDateKey = toGameDateKey(gameSlug, date);
    return statusByGameDateRef.current.get(gameDateKey) ?? null;
  }, []);

  const updateGameStatus = useCallback((gameSlug, date, status) => {
    syncStatusFromGameDate(gameSlug, date, status, 'local_update', true);
  }, [syncStatusFromGameDate]);

  const invalidateStatuses = useCallback(() => {
    const now = 0;
    for (const scope of scopeStateRef.current.values()) {
      scope.lastFetchedAt = now;
      scopeStateRef.current.set(scope.key, scope);
    }
    scheduleFetch('high');
  }, [scheduleFetch]);

  const invalidateScope = useCallback((scopeKey, priority = 'medium') => {
    const scope = scopeStateRef.current.get(scopeKey);
    if (!scope) return;
    scope.lastFetchedAt = 0;
    scopeStateRef.current.set(scopeKey, scope);
    scheduleFetch(priority);
  }, [scheduleFetch]);

  const clearCache = useCallback(() => {
    statusByPuzzleIdRef.current = new Map();
    puzzleIdByGameDateRef.current = new Map();
    statusByGameDateRef.current = new Map();

    for (const scope of scopeStateRef.current.values()) {
      scope.hasAnyData = false;
      scope.hasFetchedOnce = false;
      scope.lastFetchedAt = 0;
      scope.loadingCount = 0;
      scopeStateRef.current.set(scope.key, scope);
    }

    setIsLoading(false);
    setError(null);
    bumpVersion();
  }, [bumpVersion]);

  // Revalidate active scopes on window focus if soft stale.
  useEffect(() => {
    const handleFocus = () => {
      refreshActiveScopesIfStale('focus');
    };

    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [refreshActiveScopesIfStale]);

  // Revalidate scopes after auth state changes.
  useEffect(() => {
    if (isPuzzleInstancesLoading) return;
    scheduleFetch('high');
  }, [isSignedIn, isPuzzleInstancesLoading, scheduleFetch]);

  // Optional cross-tab status updates for local optimistic writes.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
      return undefined;
    }

    const channel = new BroadcastChannel(STATUS_CHANNEL_NAME);
    broadcastChannelRef.current = channel;

    channel.onmessage = (event) => {
      const payload = event?.data;
      if (!payload || !payload.gameSlug || !payload.date || !payload.status) {
        return;
      }
      syncStatusFromGameDate(payload.gameSlug, payload.date, payload.status, 'broadcast', false);
    };

    return () => {
      broadcastChannelRef.current = null;
      channel.close();
    };
  }, [syncStatusFromGameDate]);

  useEffect(() => () => {
    if (fetchTimerRef.current) {
      clearTimeout(fetchTimerRef.current);
      fetchTimerRef.current = null;
    }
    immediateFlushQueuedRef.current = false;
  }, []);

  const value = {
    getGameStatus,
    updateGameStatus,
    invalidateStatuses,
    invalidateScope,
    clearCache,
    registerScope,
    unregisterScopeConsumer,
    ensureScopeData,
    requestScopeRefresh,
    getScopeFreshness,
    isLoading,
    error,
    version,
  };

  return (
    <GameStatusesContext.Provider value={value}>
      {children}
    </GameStatusesContext.Provider>
  );
}

/**
 * Hook to access game statuses context
 */
export function useGameStatusesContext() {
  const context = useContext(GameStatusesContext);
  if (!context) {
    // Return safe defaults when no provider exists (admin portal)
    return {
      getGameStatus: () => null,
      updateGameStatus: () => {},
      invalidateStatuses: () => {},
      invalidateScope: () => {},
      clearCache: () => {},
      registerScope: () => {},
      unregisterScopeConsumer: () => {},
      ensureScopeData: () => {},
      requestScopeRefresh: () => {},
      getScopeFreshness: () => ({
        hasData: false,
        hasFetchedOnce: false,
        isLoading: false,
        isSoftStale: true,
        isHardStale: true,
        lastFetchedAt: 0,
      }),
      isLoading: false,
      error: null,
      version: 0,
    };
  }
  return context;
}

/**
 * Hook that mimics the old useGameStatuses interface but uses the context.
 *
 * @param {Array} gameData - Array of { gameId, games: [{ date, ... }] } objects
 * @param {boolean} enabled - Whether fetching/subscription is enabled
 * @param {object} options
 * @param {string} options.scopeKey - Scope key for status registration
 * @param {'low'|'medium'|'high'} options.priority - Fetch priority
 * @param {boolean} options.subscribeOnly - Register scope but do not actively fetch
 */
export function useGameStatuses(gameData = null, enabled = true, options = {}) {
  const {
    getGameStatus,
    registerScope,
    unregisterScopeConsumer,
    ensureScopeData,
    getScopeFreshness,
    requestScopeRefresh,
    error,
    version,
  } = useGameStatusesContext();

  const { isLoading: isPuzzleInstancesLoading } = usePuzzleInstances();
  const consumerIdRef = useRef(`consumer_${Math.random().toString(36).slice(2)}`);

  const scopeKey = options.scopeKey || (gameData && gameData.length > 0 ? `legacy:${hashGameData(gameData)}` : null);
  const priority = options.priority || 'medium';
  const subscribeOnly = options.subscribeOnly === true;

  useEffect(() => {
    if (!enabled) return undefined;
    if (isPuzzleInstancesLoading) return undefined;
    if (!scopeKey || !gameData || gameData.length === 0) return undefined;

    const consumerId = consumerIdRef.current;
    registerScope(scopeKey, consumerId, gameData, priority);

    if (!subscribeOnly) {
      ensureScopeData(scopeKey, { priority });
    }

    return () => {
      unregisterScopeConsumer(scopeKey, consumerId);
    };
  }, [
    enabled,
    isPuzzleInstancesLoading,
    scopeKey,
    gameData,
    priority,
    subscribeOnly,
    registerScope,
    ensureScopeData,
    unregisterScopeConsumer,
  ]);

  const scopeFreshness = scopeKey ? getScopeFreshness(scopeKey) : {
    hasData: false,
    hasFetchedOnce: false,
    isLoading: false,
    isSoftStale: true,
    isHardStale: true,
    lastFetchedAt: 0,
  };

  const effectiveLoading = !enabled
    ? false
    : (
      isPuzzleInstancesLoading
      || scopeFreshness.isLoading
      || (!!scopeKey && !scopeFreshness.hasFetchedOnce)
    );

  return {
    getGameStatus,
    isLoading: effectiveLoading,
    error,
    version,
    scopeState: scopeFreshness,
    requestScopeRefresh: (override = {}) => {
      if (!scopeKey) return;
      requestScopeRefresh(scopeKey, {
        priority: override.priority || priority,
        force: override.force === true,
      });
    },
  };
}
